import { Component, EventEmitter, OnInit, Output, Input, inject } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { DomSanitizer } from '@angular/platform-browser';
import { ToastService } from '../../../_core/services/toast.service';
import { images } from '../../data/image';
import heic2any from 'heic2any';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';


const blobToBase64 = async (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read blob as Base64.'));
    reader.readAsDataURL(blob);
  });
};
const resizeImage = async (image: HTMLImageElement, width: number, height: number): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context.');
  }
  const scale = Math.min(width / image.width, height / image.height);
  canvas.width = image.width * scale;
  canvas.height = image.height * scale;
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to resize image.'));
      }
    }, 'image/jpeg', 0.9);
  });
};

@Component({
  standalone: false,
  selector: 'app-upload-image',
  templateUrl: './upload-image.component.html',
  styleUrls: ['./upload-image.component.scss'],
})
export class UploadImageComponent implements OnInit {
  private translateService = inject(TranslateService);
  @Output() imageSelected = new EventEmitter<{
    image: any;
    imageChanged: boolean;
  }>();
  @Input() public userImage: string | undefined;
  public imageUrl: any = '';
  public isMobile: boolean;
  public imageChangedEvent: Event | null = null;
  public _croppedImage: any = '';
  public isModalOpen = false;
  public imageBase64: string | null = null;
  public errorMessage: string = '';
  @Input() public cropImage: boolean = true;
  @Input({required: false}) public disableImageResize: boolean = false;
  // Allowed file types
  public allowedFormats = [
    'image/jpeg',
    'image/png',
    'image/jpg',
    'image/heic',
  ];
  public avatarImage = images.avatarImage;

  constructor(
    private platform: Platform,
    private sanitizer: DomSanitizer,
    private toastService: ToastService
  ) {
    this.isMobile = this.platform.is('android') || this.platform.is('ios');
  }

  ngOnInit() {}

  ngAfterContentInit() {
    if (this.userImage) {
      this.imageUrl = this.userImage;
    }
  }

  /* Take a  picture on mobile device*/
  async takePicture() {
    const platform = Capacitor.getPlatform();
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.Base64,
      source: this.isMobile && this.platform.is('cordova') ? CameraSource.Prompt : CameraSource.Photos,
    });

    const base64Image = `data:image/${image.format};base64,${image.base64String}`;
    let blob = await fetch(base64Image).then((res) => res.blob());
    try {
      const res = await heic2any({
        blob,
        toType: "image/jpeg",
        quality: 0.5,
      });
      blob = Array.isArray(res) ? res[0] : res;
    } catch (err) {
    }

    const handleImage = async (blob: Blob) => {
      const img = new Image();
      img.src = URL.createObjectURL(blob);
      try {
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = () => reject(new Error('Failed to load image.'));
        });
        //const resizedBlob = await resizeImage(img, 512, 512);
        let processedBlob: Blob;
        if (this.disableImageResize) {
          processedBlob = blob;
        } else {
          processedBlob = await resizeImage(img, 512, 512);
        }
        const finalBase64Img = await blobToBase64(processedBlob);
        const file = new File([processedBlob], 'captured-image.jpg', { type: 'image/jpeg' });
        if (!this.allowedFormats.includes(file.type)) {
          this.toastService.presentDangerToast(this.translateService.instant('Invalid file format. Only JPG, PNG, HEIC are allowed.'));
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          this.toastService.presentDangerToast(this.translateService.instant('File size exceeds 5MB. Please upload a smaller file.'));
          return;
        }
        this.imageChangedEvent = { target: { files: [file] } } as unknown as Event;
        this.imageBase64 = finalBase64Img;
        if(!this.cropImage){
          this.emitFile(file);
        return
       }
        this.setOpen(true);
        setTimeout(() => {
          this.imageChangedEvent = null;
        }, 100);
      } catch (error) {
        this.toastService.presentDangerToast(this.translateService.instant('Invalid file format. Only JPG, PNG, HEIC are allowed.'));
      } finally {
        URL.revokeObjectURL(img.src);
      }
    };
    handleImage(blob);
  }
    

  async convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  }

  /* once select a image form web*/
  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    let file = input.files[0];

    if (input.files && input.files[0]) {
      if (
        input.files[0].type !== '' &&
        !this.allowedFormats.includes(input.files[0].type.toLowerCase())
      ) {
        this.toastService.presentDangerToast(
          this.translateService.instant('Invalid file format. Only JPG, PNG are allowed.')
        );
        return;
      }

      if (input.files[0].size > 5 * 1024 * 1024) {
        this.toastService.presentDangerToast(
          this.translateService.instant('File size exceeds 5MB. Please upload a smaller file.')
        );
        return;
      }

      if (
        file.type === 'image/heic' ||
        file.name.toLowerCase().endsWith('.heic')
      ) {
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
          });

          // Handle the case where an array of blobs is returned
          const blob = Array.isArray(convertedBlob)
            ? convertedBlob[0]
            : convertedBlob;

          if (!(blob instanceof Blob)) {
            console.error('Blob conversion failed', convertedBlob);
            this.toastService.presentDangerToast(this.translateService.instant('HEIC conversion failed.'));
            return;
          }

          // Convert Blob to File
          file = new File([blob], file.name.replace('.heic', '.jpeg'), {
            type: 'image/jpeg',
          });

          // Convert to Base64
          this.imageBase64 = await this.blobToBase64(blob);
        } catch (error) {
          this.toastService.presentDangerToast(this.translateService.instant('Error converting HEIC file.'));
          return;
        }
      } else {
        this.imageChangedEvent = event;
      }
       if(!this.cropImage){
        this.emitFile(file);
        return
       } 
       this.setOpen(true);
    }
    let cleatTimeout = setTimeout(() => {
      input.value = '';
      clearTimeout(cleatTimeout);
    }, 100);
  }

  emitFile(file:File){
    this.imageUrl = window.URL.createObjectURL(file);
        let uploadedData = {
          image: this.imageUrl,
          imageChanged:
            true
        };
    
        this.imageSelected.emit(uploadedData);

  }

  /* This function is calling when user cropping an image from WEB.*/
  imageCropped(event: ImageCroppedEvent) {
    if (event.objectUrl) {
      this._croppedImage = this.sanitizer.bypassSecurityTrustUrl(
        event.objectUrl
      );
    }
  }

  /* This function is calling when user cropping an image from MOBILE.*/
  onImageCropped(event: ImageCroppedEvent) {
    this._croppedImage = event.base64;
  }

  /* If image load failed.*/
  loadImageFailed() {
    this.setOpen(false);
  }

  /* Modal open and close.*/
  setOpen(isOpen: boolean) {
    this.isModalOpen = isOpen;
    if (!isOpen) {
      this._croppedImage = '';
    }
  }

  /* After cropped image save data.*/
  saveImage() {
    this.imageUrl = this._croppedImage;

    let uploadedData = {
      image: this._croppedImage?.changingThisBreaksApplicationSecurity,
      imageChanged:
        this._croppedImage?.changingThisBreaksApplicationSecurity.length > 1
          ? true
          : false,
    };

    this.imageSelected.emit(uploadedData);
    this.setOpen(false);
  }

  /* Clear the image if user selected.*/
  clearImage() {
    this.imageUrl = '';
    this.imageSelected.emit({ image: null, imageChanged: true });
  }
}
